import type { CommonData } from '@/components/blockEditor/blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from '@/components/blockEditor/blockEditor.types';

export function getDefaultInstallmentSaleOverviewPageOneData(): CommonData {
  return {
    version: CURRENT_SCHEMA_VERSION,
    blocks: [
      {
        id: 'description',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'installment-sale-overview-heading',
              type: 'EducationalSlideHeading',
              props: {
                title: 'What is an installment sale?',
                variant: 'h3',
              },
            },
            {
              id: 'installment-sale-overview-body',
              type: 'EditableEducationalSlideBody',
              props: {
                text: "An installment sale is a wealth transfer technique used to transfer assets outside of the grantor's estate. Installment sales are typically used to \"freeze\" the size of an estate by transferring appreciation of assets out of the grantor's taxable estate during the lifetime of the grantor, with any appreciation being free of gift and estate tax.\n\nTypically, the sale is structured as a promissory note in which the note recipient makes annual interest-only payments, with a balloon payment of the principal balance at the end of the note term. This structure maximizes the assets held outside of the grantor's estate, which may allow for more appreciation to occur in the recipient trust. **Assets with high growth potential are common candidates for installment sales.**",
              },
            },
          ],
        },
      },
      {
        id: 'benefits',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'installment-sale-page-one-columns',
              type: 'TwoColumnLayout',
              props: {
                leftWidth: '50%',
                rightWidth: '50%',
                columnGap: 2,
                showDivider: true,
              },
              children: {
                leftColumn: [
                  {
                    id: 'installment-sale-benefits-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Potential benefits',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'installment-sale-benefit-estate',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Estate tax reduction',
                      description:
                        'Installment sales move asset appreciation and future income out of the estate, which can significantly reduce future estate tax liability.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'installment-sale-benefit-discounts',
                    type: 'PresentationListItem',
                    props: {
                      iconColor: 'PRIMARY',
                      title: 'Pursuing discounts',
                      description:
                        'Using assets that are privately-held and illiquid can maximize the strategy through discounted valuations. By using a discount, the value of the gift and note are reduced for transfer tax purposes, increasing the corresponding investment return that occurs outside of the grantor’s estate.',
                      iconType: 'check',
                    },
                  },
                  {
                    id: 'installment-sale-benefit-gst',
                    type: 'PresentationListItem',
                    props: {
                      title: 'GST efficiency',
                      description:
                        'The purchasing trust can be structured to be GST-exempt to further leverage wealth transfer tax savings for multiple generations.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'installment-sale-benefit-tax-free-growth',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Income tax-free growth',
                      description:
                        'Taxes generated by the trust are paid by the grantor using assets outside of the trust, further reducing potential estate tax exposure. Note payments made to the grantor are not taxable.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                ],
                rightColumn: [
                  {
                    id: 'installment-sale-drawbacks-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Potential drawbacks',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'installment-sale-drawback-taxable-gift',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Taxable gift',
                      description:
                        "An installment sale may require using the grantor's lifetime exemption to seed the purchasing trust. If the grantor has no exemption remaining, seeding the strategy with a taxable gift may still be worthwhile.",
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'installment-sale-drawback-lifetime',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Grantor lifetime considerations',
                      description:
                        "If the grantor dies before the note is fully paid off, the note receivable is an asset included in the grantor's taxable estate.",
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'installment-sale-drawback-scrutiny',
                    type: 'PresentationListItem',
                    props: {
                      title: 'IRS scrutiny',
                      description:
                        'While installment sales are frequently recommended by estate planning professionals, these transactions are subject to higher IRS scrutiny as the strategy is not explicitly defined by IRS code.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'installment-sale-drawback-complexity',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Complexity & costs',
                      description:
                        'Creating and maintaining an installment sale involves legal and administrative expenses, and the structure may require ongoing professional management.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    ],
  };
}
